import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, ArrowRight, Calendar } from "lucide-react";
import { BRAND } from "@/lib/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Confirmation page reached from the teacher's notification email.
 * URL: /confirmar-agendamento?token=…&slot=N
 *
 * Flow:
 * 1. On mount, GET /api/bookings/preview?token=…  → renders booking summary.
 * 2. Prof Lionel sees the 3 slot options; the one pre-selected via ?slot=N
 *    is highlighted. He can switch to another slot before confirming.
 * 3. "Confirmar" → POST /api/bookings/confirm  → success screen.
 * 4. "Recusar todos" → POST /api/bookings/cancel  → cancellation screen.
 */
export default function ConfirmBooking() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const initialSlot = Math.max(0, Math.min(2, parseInt(params.get("slot") || "0", 10)));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(initialSlot);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // 'confirmed' | 'cancelled' | 'already_confirmed'
  const [confirmedLabel, setConfirmedLabel] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Token ausente na URL.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/bookings/preview`, {
          params: { token },
        });
        if (cancelled) return;
        setPreview(data);
        // If backend says it's already confirmed, jump straight to result view
        if (data.status === "confirmed") {
          setResult("already_confirmed");
          if (data.confirmed_slot_index != null && data.slots[data.confirmed_slot_index]) {
            setConfirmedLabel(data.slots[data.confirmed_slot_index].label);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.detail || "Não foi possível carregar o agendamento.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const onConfirm = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/bookings/confirm`, {
        token,
        slot_index: selected,
      });
      setResult(data.status);
      setConfirmedLabel(data.slot_label || preview.slots[selected].label);
      toast.success("Confirmado ✓ o aluno recebeu o convite .ics");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao confirmar.");
    } finally {
      setSubmitting(false);
    }
  };

  const onCancel = async () => {
    if (!window.confirm("Recusar todos os horários e avisar o aluno?")) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings/cancel`, { token });
      setResult("cancelled");
      toast.success("Aluno avisado por e-mail.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao recusar.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#f4f1ea] text-[#1e2a5a] font-body"
      data-testid="confirm-booking-page"
    >
      <header className="border-b border-[#e5e3db] bg-white/60 backdrop-blur">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="font-display text-lg" data-testid="confirm-booking-brand">
            {BRAND.name}
          </Link>
          <span className="text-xs uppercase tracking-[0.18em] text-[#9c9c9c]">
            Painel de confirmação
          </span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        {loading && (
          <div className="flex items-center gap-3 text-[#5c5c5c]">
            <Loader2 className="h-5 w-5 animate-spin" />
            Carregando agendamento…
          </div>
        )}

        {!loading && error && (
          <ErrorCard message={error} />
        )}

        {!loading && !error && preview && !result && (
          <BookingPreview
            preview={preview}
            selected={selected}
            setSelected={setSelected}
            onConfirm={onConfirm}
            onCancel={onCancel}
            submitting={submitting}
          />
        )}

        {!loading && !error && (result === "confirmed" || result === "already_confirmed") && (
          <SuccessCard
            already={result === "already_confirmed"}
            leadName={preview?.name}
            slotLabel={confirmedLabel}
          />
        )}

        {!loading && !error && result === "cancelled" && (
          <CancelledCard leadName={preview?.name} />
        )}
      </main>
    </div>
  );
}

// -------- sub-components --------

const BookingPreview = ({
  preview,
  selected,
  setSelected,
  onConfirm,
  onCancel,
  submitting,
}) => (
  <article
    data-testid="booking-preview"
    className="bg-white rounded-2xl border border-[#e5e3db] overflow-hidden"
  >
    <div className="p-8 border-b border-[#e5e3db]">
      <p className="overline">Novo pedido de aula</p>
      <h1 className="font-display text-3xl sm:text-4xl tracking-tight text-[#1e2a5a] mt-3">
        {preview.name} quer marcar aula com você
      </h1>
      {preview.message && (
        <p className="mt-4 text-sm text-[#3a3a3a] border-l-2 border-[#d9381e] pl-4">
          {preview.message}
        </p>
      )}
    </div>

    <div className="p-8">
      <p className="text-xs uppercase tracking-[0.18em] text-[#5c5c5c] mb-4">
        Escolha o horário que funciona pra você
      </p>
      <ul className="flex flex-col gap-3">
        {preview.slots.map((s, i) => {
          const active = i === selected;
          return (
            <li key={`${s.start}-${i}`}>
              <button
                type="button"
                onClick={() => setSelected(i)}
                data-testid={`confirm-slot-${i}`}
                className={`w-full text-left rounded-xl border transition-colors p-5 ${
                  active
                    ? "border-[#1e2a5a] bg-[#f4f1ea]"
                    : "border-[#e5e3db] bg-white hover:border-[#1e2a5a]/40"
                }`}
              >
                <div className="flex items-center gap-3">
                  <span
                    className={`h-5 w-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                      active ? "border-[#d9381e]" : "border-[#e5e3db]"
                    }`}
                  >
                    {active && <span className="h-2 w-2 rounded-full bg-[#d9381e]" />}
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-[0.14em] text-[#9c9c9c] mb-1">
                      Opção {i + 1}
                    </p>
                    <p className="font-medium text-[#1e2a5a]">{s.label}</p>
                  </div>
                </div>
              </button>
            </li>
          );
        })}
      </ul>

      <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <button
          type="button"
          onClick={onConfirm}
          disabled={submitting}
          data-testid="confirm-booking-button"
          className="inline-flex items-center gap-2 rounded-full bg-[#1e2a5a] text-[#f4f1ea] px-6 py-3.5 text-sm font-medium hover:bg-[#d9381e] transition-colors disabled:opacity-60"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Confirmando…
            </>
          ) : (
            <>
              Confirmar opção {selected + 1}
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          data-testid="cancel-booking-button"
          className="text-sm text-[#9c9c9c] hover:text-[#d9381e] transition-colors"
        >
          Nenhum horário funciona — recusar
        </button>
      </div>
      <p className="mt-6 text-xs text-[#9c9c9c] leading-relaxed">
        Ao confirmar, o aluno recebe um e-mail com o convite <code>.ics</code>{" "}
        para adicionar ao calendário. Você também recebe uma cópia.
      </p>
    </div>
  </article>
);

const SuccessCard = ({ already, leadName, slotLabel }) => (
  <div
    data-testid="booking-success-card"
    className="bg-white rounded-2xl border border-[#e5e3db] p-10"
  >
    <CheckCircle2 className="h-10 w-10 text-[#d9381e] mb-4" />
    <h1 className="font-display text-3xl sm:text-4xl text-[#1e2a5a] mb-4">
      {already ? "Agendamento já confirmado" : "Agendamento confirmado ✓"}
    </h1>
    <p className="text-[#3a3a3a] leading-relaxed">
      {already
        ? "Este agendamento já tinha sido confirmado anteriormente."
        : `${leadName} já recebeu o e-mail com o convite .ics.`}
    </p>
    {slotLabel && (
      <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#f4f1ea] px-4 py-2 text-sm text-[#1e2a5a]">
        <Calendar className="h-4 w-4 text-[#d9381e]" />
        {slotLabel}
      </div>
    )}
    <div className="mt-8">
      <Link
        to="/admin/leads"
        className="text-sm text-[#1e2a5a] hover:text-[#d9381e] transition-colors underline underline-offset-4"
      >
        Ver todos os agendamentos no admin →
      </Link>
    </div>
  </div>
);

const CancelledCard = ({ leadName }) => (
  <div
    data-testid="booking-cancelled-card"
    className="bg-white rounded-2xl border border-[#e5e3db] p-10"
  >
    <XCircle className="h-10 w-10 text-[#9c9c9c] mb-4" />
    <h1 className="font-display text-3xl sm:text-4xl text-[#1e2a5a] mb-4">
      Agendamento recusado
    </h1>
    <p className="text-[#3a3a3a] leading-relaxed">
      {leadName ? `${leadName} foi avisado(a) por e-mail.` : "O aluno foi avisado por e-mail."}
    </p>
  </div>
);

const ErrorCard = ({ message }) => (
  <div
    data-testid="booking-error-card"
    className="bg-white rounded-2xl border border-[#e5e3db] p-10"
  >
    <XCircle className="h-10 w-10 text-[#d9381e] mb-4" />
    <h1 className="font-display text-3xl text-[#1e2a5a] mb-3">Link inválido</h1>
    <p className="text-[#3a3a3a] leading-relaxed">{message}</p>
    <p className="mt-4 text-sm text-[#9c9c9c]">
      Se o link expirou (72h), peça ao aluno para reenviar o pedido.
    </p>
  </div>
);
